import { type FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { showToast } from "@/helpers/toast"
import { useModals } from "@/modals"
import {
  useJoinFriendLobbyMutation,
  usePreviewFriendLobbyQuery
} from "@/store/apis/friendLobbyAPI"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "../../PlayWithFriendModal.module.scss"

export const JoinerView: FC<{ token: string; onClose: () => void }> = ({
  token,
  onClose
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { closeAllModals } = useModals()

  const { data, isLoading, isError, error } = usePreviewFriendLobbyQuery(token)

  const [joinFriendLobby, { isLoading: isJoining }] =
    useJoinFriendLobbyMutation()

  const getErrorText = (status?: number) => {
    switch (status) {
      case 404:
        return t("friendLobby.errors.notFound")
      case 410:
        return t("friendLobby.errors.expired")
      case 422:
        return t("friendLobby.errors.ownLobby")
      case 409:
        return t("friendLobby.errors.inMatch")
      case 503:
        return t("friendLobby.errors.unavailable")
      default:
        return t("friendLobby.errors.notFound")
    }
  }

  const errorMessage = useMemo(() => {
    if (!isError || !error) return null

    const status = (error as { status?: number })?.status

    return getErrorText(status)
  }, [isError, error, t])

  const handleJoin = async () => {
    try {
      const data = await joinFriendLobby(token)

      if (data.data) {
        navigate(`/match/${data.data.sessionId}`, { replace: true })
        closeAllModals()
      } else {
        showToast.error(getErrorText((data?.error as any)?.status))
      }
    } catch {
      showToast.error(t("message.somethingWentWrong"))
    }
  }

  const creatorLabel = data?.creatorName
    ? t("friendLobby.creatorLabel", { name: data.creatorName })
    : t("friendLobby.unknownCreator")

  return (
    <Modal.StoneFlexContainer
      className={styles.PlayWithFriendModal_container}
      content={{ className: styles.PlayWithFriendModal_content }}
      outside={
        <div className={styles.PlayWithFriendModal_footerButtons}>
          <Button
            className={styles.PlayWithFriendModal_footerButtons_item}
            styles="green"
            onClick={onClose}
          >
            {t("button.close")}
          </Button>
          {!isError && (
            <Button
              className={styles.PlayWithFriendModal_footerButtons_item}
              styles="orange"
              onClick={handleJoin}
              isLoading={isJoining || isLoading}
              disabled={isLoading}
            >
              {t("button.join")}
            </Button>
          )}
        </div>
      }
    >
      <OutlinedText className={styles.PlayWithFriendModal_title}>
        {t("playWithFriend.title")}
      </OutlinedText>
      {isLoading && (
        <div className={styles.PlayWithFriendModal_waiting}>...</div>
      )}
      {isError && (
        <div className={styles.PlayWithFriendModal_error}>{errorMessage}</div>
      )}
      {!isLoading && !isError && (
        <div className={styles.PlayWithFriendModal_waiting}>{creatorLabel}</div>
      )}
    </Modal.StoneFlexContainer>
  )
}
